/**
 * Recently saved event ids, backing the details panel's "change room"
 * cooldown. A composer save's calendar sync is asynchronous, and `/move`
 * copies the SERVER-side event — moving a room right after a save can copy
 * a pre-save version (the staging race that killed move-at-save). The
 * details panel disables "change room" while an event's save is younger
 * than the cooldown.
 *
 * localStorage-backed so the guard survives a refresh and covers other
 * tabs — the save-then-refresh-then-move user is exactly who it exists
 * for — degrading to an in-memory map where storage is unavailable
 * (private browsing, quota), which is simply the pre-persistence behavior.
 *
 * Written from the save path (performSave) rather than a channel
 * subscription because the details panel is usually unmounted at submit
 * time. This store is the surviving core of a broader "calendar sync
 * journal" (pending-move banner/gating, post-refresh poll restart) removed
 * after field testing — see move-organizer-space-plan.md before growing it
 * again. Ids and timestamps only, never event content.
 */
var __values = (this && this.__values) || function(o) {
    var s = typeof Symbol === "function" && Symbol.iterator, m = s && o[s], i = 0;
    if (m) return m.call(o);
    if (o && typeof o.length === "number") return {
        next: function () {
            if (o && i >= o.length) o = void 0;
            return { value: o && o[i++], done: !o };
        }
    };
    throw new TypeError(s ? "Object is not iterable." : "Symbol.iterator is not defined.");
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
export var RECENT_SAVE_COOLDOWN_MS = 60000;
var STORAGE_KEY = 'rbn.recent-event-saves';
var CHANGE_EVENT = 'rbn:recent-event-saves-change';
// --- Storage backend (localStorage with in-memory fallback) ---
var memorySaves = {};
var storageAvailable = null;
var hasLocalStorage = function () {
    if (storageAvailable !== null)
        return storageAvailable;
    try {
        var probeKey = "".concat(STORAGE_KEY, ".probe");
        window.localStorage.setItem(probeKey, '1');
        window.localStorage.removeItem(probeKey);
        storageAvailable = true;
    }
    catch (_a) {
        storageAvailable = false;
    }
    return storageAvailable;
};
var readSaves = function (now) {
    var e_1, _a;
    var saves;
    if (hasLocalStorage()) {
        try {
            var raw = window.localStorage.getItem(STORAGE_KEY);
            var parsed = raw ? JSON.parse(raw) : {};
            saves = parsed && typeof parsed === 'object' ? parsed : {};
        }
        catch (_b) {
            // Corrupt payload — TTL-bounded guard state, discarding is safe.
            saves = {};
        }
    }
    else {
        saves = memorySaves;
    }
    var pruned = {};
    try {
        for (var _c = __values(Object.entries(saves)), _d = _c.next(); !_d.done; _d = _c.next()) {
            var _e = __read(_d.value, 2), id = _e[0], savedAt = _e[1];
            if (typeof savedAt === 'number' &&
                now - savedAt <= RECENT_SAVE_COOLDOWN_MS) {
                pruned[id] = savedAt;
            }
        }
    }
    catch (e_1_1) { e_1 = { error: e_1_1 }; }
    finally {
        try {
            if (_d && !_d.done && (_a = _c.return)) _a.call(_c);
        }
        finally { if (e_1) throw e_1.error; }
    }
    return pruned;
};
var writeSaves = function (saves) {
    if (hasLocalStorage()) {
        try {
            window.localStorage.setItem(STORAGE_KEY, JSON.stringify(saves));
        }
        catch (_a) {
            storageAvailable = false;
            memorySaves = saves;
        }
    }
    else {
        memorySaves = saves;
    }
    emitChange();
};
// --- Change notification ---
//
// Three paths, because each host bundle (shell, schedule, office) holds its
// OWN instance of this module while sharing one localStorage:
//   - same module instance and same tab, other bundle: a window event (the
//     'storage' event does NOT fire in the tab that wrote)
//   - other tabs: the 'storage' event
// Writes dispatch the window event; every instance listens to it (plus
// 'storage') and notifies its own subscribers.
var version = 0;
var listeners = [];
var windowListenersRegistered = false;
var notifyLocalListeners = function () {
    version++;
    listeners.forEach(function (listener) { return listener(); });
};
var emitChange = function () {
    if (typeof window !== 'undefined') {
        // Reaches this module instance too, via its own CHANGE_EVENT listener.
        window.dispatchEvent(new Event(CHANGE_EVENT));
    }
    else {
        notifyLocalListeners();
    }
};
var registerWindowListeners = function () {
    if (windowListenersRegistered || typeof window === 'undefined')
        return;
    windowListenersRegistered = true;
    window.addEventListener(CHANGE_EVENT, notifyLocalListeners);
    window.addEventListener('storage', function (event) {
        if (event.key === STORAGE_KEY) {
            notifyLocalListeners();
        }
    });
};
/**
 * Subscribe to store changes — this tab's writes from ANY bundle (window
 * event) and other tabs' (the `storage` event). Pair with
 * `getRecentSavesVersion` for `useSyncExternalStore`.
 */
export var subscribeToRecentSaves = function (listener) {
    registerWindowListeners();
    listeners = __spreadArray(__spreadArray([], __read(listeners), false), [listener], false);
    return function () {
        listeners = listeners.filter(function (l) { return l !== listener; });
    };
};
/** Monotonic change counter — the `useSyncExternalStore` snapshot. */
export var getRecentSavesVersion = function () { return version; };
// --- The cooldown itself ---
/**
 * Record a successful composer save. Both ids are passed because
 * "this and following" recurring edits mint a new event id — either may be
 * the one a details panel is opened on.
 */
export var recordEventSave = function () {
    var e_2, _a;
    var eventIds = [];
    for (var _i = 0; _i < arguments.length; _i++) {
        eventIds[_i] = arguments[_i];
    }
    var ids = eventIds.filter(function (id) { return !!id; });
    if (!ids.length)
        return;
    var now = Date.now();
    var saves = readSaves(now);
    try {
        for (var ids_1 = __values(ids), ids_1_1 = ids_1.next(); !ids_1_1.done; ids_1_1 = ids_1.next()) {
            var id = ids_1_1.value;
            saves[id] = now;
        }
    }
    catch (e_2_1) { e_2 = { error: e_2_1 }; }
    finally {
        try {
            if (ids_1_1 && !ids_1_1.done && (_a = ids_1.return)) _a.call(ids_1);
        }
        finally { if (e_2) throw e_2.error; }
    }
    writeSaves(saves);
};
/** 0 when no cooldown applies; otherwise ms until it lapses. */
export var msUntilSaveCooldownEnds = function (eventId) {
    var now = Date.now();
    var savedAt = readSaves(now)[eventId];
    if (savedAt === undefined)
        return 0;
    return Math.max(0, savedAt + RECENT_SAVE_COOLDOWN_MS - now);
};
